/**
 * Rich text editor for blog body using TipTap.
 * Supports bold, italic, lists, links, and images (URL or upload when endpoint available).
 */

import React, { useEffect, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';

const inputStyle = {
  width: '100%',
  padding: '10px',
  borderRadius: '8px',
  border: '1px solid var(--border-color)',
  background: 'var(--background)',
  color: 'var(--text-primary)',
  minHeight: '200px'
};

const toolbarStyle = {
  display: 'flex',
  gap: '6px',
  flexWrap: 'wrap',
  marginBottom: '8px',
  padding: '8px',
  background: 'var(--surface-color)',
  borderRadius: '8px',
  border: '1px solid var(--border-color)'
};

const btnStyle = {
  padding: '6px 10px',
  borderRadius: '6px',
  border: '1px solid var(--border-color)',
  background: 'var(--background)',
  color: 'var(--text-primary)',
  cursor: 'pointer',
  fontSize: '14px'
};

const RichTextEditor = ({ content = '', onChange, placeholder = 'Write your post...', onImageUpload }) => {
  // ProseMirror editor area
  useEffect(() => {
    const style = document.getElementById('richtext-editor-styles');
    if (style) return;
    const el = document.createElement('style');
    el.id = 'richtext-editor-styles';
    el.textContent = `
      .rich-text-editor .ProseMirror { min-height: 200px; padding: 12px; outline: none; }
      .rich-text-editor .ProseMirror p { margin: 0 0 0.5em 0; }
      .rich-text-editor .ProseMirror img { max-width: 100%; height: auto; }
    `;
    document.head.appendChild(el);
    return () => { const s = document.getElementById('richtext-editor-styles'); if (s) s.remove(); };
  }, []);
  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({ inline: false, allowBase64: false }),
      Link.configure({ openOnClick: false, HTMLAttributes: { target: '_blank', rel: 'noopener' } })
    ],
    content: content || '',
    editorProps: {
      attributes: {
        style: 'min-height: 200px; padding: 10px; outline: none;'
      }
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    }
  });

  useEffect(() => {
    if (editor && content !== undefined && content !== editor.getHTML()) {
      editor.commands.setContent(content || '', false);
    }
  }, [content]); // eslint-disable-line react-hooks/exhaustive-deps

  const addImage = useCallback(() => {
    if (!editor) return;
    if (onImageUpload && typeof onImageUpload === 'function') {
      onImageUpload((url) => {
        editor.chain().focus().setImage({ src: url }).run();
      });
      return;
    }
    const url = window.prompt('Image URL:');
    if (url) editor.chain().focus().setImage({ src: url }).run();
  }, [editor, onImageUpload]);

  if (!editor) return <div style={inputStyle}>Loading editor…</div>;

  return (
    <div className="rich-text-editor" style={{ border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden' }}>
      <div style={toolbarStyle}>
        <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} style={btnStyle} title="Bold">
          <strong>B</strong>
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} style={btnStyle} title="Italic">
          <em>I</em>
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} style={btnStyle} title="Bullet list">• List</button>
        <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} style={btnStyle} title="Numbered list">1. List</button>
        <button type="button" onClick={() => { const u = window.prompt('Link URL:'); if (u) editor.chain().focus().setLink({ href: u }).run(); }} style={btnStyle} title="Link">Link</button>
        <button type="button" onClick={addImage} style={btnStyle} title="Image">Image</button>
      </div>
      <EditorContent editor={editor} style={inputStyle} />
    </div>
  );
};

export default RichTextEditor;
